import React, { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Loader2, Rocket, Calendar } from "lucide-react";
import { api } from "../../lib/api";

/**
 * LaunchChecklistCard — one-screen launch readiness dashboard.
 *
 * Pulls `/api/admin/launch-checklist` and rolls 8 checks into a single
 * "WE'RE READY" / "NEEDS ATTENTION" headline. Each check shows a status
 * dot (green ok / amber warn / red bad) and a one-line detail so the
 * admin can see exactly what's outstanding without clicking around.
 *
 * Refreshes every 30s. Mounted at the top of /admin so launch morning
 * takes 5 seconds, not 5 minutes of poking through tabs.
 */
export default function LaunchChecklistCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data: res } = await api.get("/admin/launch-checklist");
      setData(res);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load checklist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => {
      if (!document.hidden) load();
    }, 30_000);
    return () => clearInterval(id);
  }, []);

  if (loading && !data) {
    return (
      <section
        className="bg-white border hairline p-5 sm:p-6 mb-6"
        data-testid="launch-checklist"
      >
        <div className="mono text-xs text-[#888] flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading launch checklist…
        </div>
      </section>
    );
  }

  if (!data) {
    return (
      <section className="bg-white border hairline p-5 mb-6">
        <div className="mono text-xs text-red-700">{err}</div>
      </section>
    );
  }

  const { ready, days_to_launch: days, is_launched, checks = [], summary = {} } = data;
  const failing = checks.filter((c) => !c.ok).length;

  return (
    <section
      className="border-2 hairline-strong bg-white p-5 sm:p-6 mb-6"
      data-testid="launch-checklist"
    >
      {/* Hero — single headline status */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="eyebrow flex items-center gap-1.5 mb-1.5"
               style={{ color: ready ? "#0a7f3f" : "#b45309" }}>
            <Rocket className="w-3 h-3" /> Launch readiness
          </div>
          <h2 className="font-display text-2xl sm:text-3xl leading-tight">
            {ready ? "We're ready 🚀" : `${failing} thing${failing > 1 ? "s" : ""} need${failing > 1 ? "" : "s"} attention`}
          </h2>
          <p className="text-xs mono text-[#666] mt-1.5 flex items-center gap-1.5">
            <Calendar className="w-3 h-3" />
            {is_launched
              ? "We've launched · live now"
              : `${days} day${days === 1 ? "" : "s"} to go · refreshes every 30s`}
          </p>
          {err && <p className="text-xs text-red-700 mt-1.5 mono">{err}</p>}
        </div>

        {/* Big OK / WARN tile */}
        <div
          className={`hidden sm:flex items-center justify-center font-display text-2xl ${
            ready ? "bg-[#0a7f3f]" : "bg-[#b45309]"
          } text-white px-4 py-3`}
          style={{ minWidth: 130 }}
        >
          {ready ? "GO" : "HOLD"}
        </div>
      </div>

      {/* Quick numbers strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
        <BigStat
          value={summary.enriched ?? 0}
          total={summary.total_sites ?? 0}
          pct={summary.pct_enriched ?? 0}
          label="Sites enriched"
          testid="launch-stat-enriched"
        />
        <BigStat
          value={summary.signups_24h ?? 0}
          label="Signups (24h)"
          testid="launch-stat-signups"
        />
        <BigStat
          value={summary.errors_24h ?? 0}
          label="Errors (24h)"
          good={(summary.errors_24h ?? 0) === 0}
          bad={(summary.errors_24h ?? 0) > 5}
          testid="launch-stat-errors"
        />
        <BigStat
          value={summary.worker_on ? "ON" : "OFF"}
          label="Enrich worker"
          testid="launch-stat-worker"
        />
      </div>

      {/* Per-check list */}
      <ul className="divide-y hairline">
        {checks.map((c) => (
          <li
            key={c.id}
            className="py-2.5 flex items-start gap-3"
            data-testid={`launch-check-${c.id}`}
          >
            <StatusDot ok={c.ok} warn={c.warn} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-[#111]">{c.label}</div>
              <div className="text-xs text-[#555] mono mt-0.5 truncate">{c.detail}</div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function StatusDot({ ok, warn }) {
  if (ok) return <CheckCircle2 className="w-4 h-4 text-[#0a7f3f] shrink-0 mt-0.5" />;
  if (warn) return <AlertTriangle className="w-4 h-4 text-[#b45309] shrink-0 mt-0.5" />;
  return <XCircle className="w-4 h-4 text-red-700 shrink-0 mt-0.5" />;
}

function BigStat({ value, total, pct, label, good, bad, testid }) {
  const fmt = typeof value === "number" ? value.toLocaleString("en-GB") : value;
  return (
    <div
      data-testid={testid}
      className={`p-3 border hairline ${good ? "bg-[#ecfdf5]" : bad ? "bg-[#fef2f2]" : "bg-[#fafafa]"}`}
    >
      <div className="eyebrow text-[10px] mb-1.5 text-[#666]">{label}</div>
      <div className="font-display text-xl leading-none tabular-nums">
        {fmt}
        {typeof total === "number" && total > 0 && (
          <span className="text-xs text-[#888] mono ml-1">/ {total.toLocaleString("en-GB")}</span>
        )}
      </div>
      {typeof pct === "number" && pct > 0 && (
        <div className="text-[10px] mono text-[#666] mt-1">{pct}%</div>
      )}
    </div>
  );
}
